import { ListItemText } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { differenceInSeconds } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import { getHolderDetailAndRequiredDocumentsRequest } from '@/api/operation';

import { StatusIcon } from '@/components/DataDisplay/StatusIcon';
import { CenterSlot } from '@/components/Header/Header.styled';
import { Loader } from '@/components/Icon/Loader';
import { DrawerProps } from '@/components/Layout/Drawer';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';

import { DOCUMENT_SIGNING_SECONDS } from '@/constants/common';

import { useGetMinorDetailAndRequiredDocuments } from '@/hooks/minor/useGetminorDetailAndRequiredDocuments';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { notifyMinorDataChanged } from '@/redux/operation';
import {
  selectOperationUser,
  selectSigningDocuments,
} from '@/redux/operation/selectors';
import { selectUserHolderJointMinor } from '@/redux/user-data';

import { Option } from '@/types/components/common';
import {
  DocumentStatus,
  SigningDocument,
  SigningFormType,
} from '@/types/document';

import {
  buildSigningDrawerDocumentLookups,
  findUserDocumentForSigningForm,
  getSigningDrawerListRowKey,
} from '@/utils/document';

import { PreQuestions } from '../components/PreQuestions';
import { SigningDocumentViewer } from '../components/SigningDocumentViewer';

import {
  DrawerContainer,
  StyledDocumentsSigningDrawer,
} from './Drawers.styled';

export function MinorDocumentSigningDrawer(props: DrawerProps) {
  const dispatch = useAppDispatch();
  const tempSelected = useRef<Option | undefined>(undefined);
  const operationUser = useAppSelector(selectOperationUser);
  const loggedInUser = useAppSelector(selectLoggedInUser);
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const { formsToSign } = useAppSelector(selectSigningDocuments);
  const [documentToSign, setDocumentToSign] = useState<SigningDocument>();
  const [updatingDocs, setUpdatingDocs] = useState<Record<string, boolean>>({});
  const {
    value: isConfirmingSignAgain,
    setTrue: showConfirmingSignAgain,
    setFalse: hideConfirmingSignAgain,
  } = useBoolean(false);
  const { t } = useTranslate();
  const { isLoading } = useGetMinorDetailAndRequiredDocuments();

  useEffect(() => {
    if (props.open) {
      setDocumentToSign(undefined);
    }
  }, [props.open]);

  const signingDrawerLookups = useMemo(
    () => buildSigningDrawerDocumentLookups(operationUser?.documents),
    [operationUser],
  );

  const { documentByFormType, documentOptions } = useMemo(() => {
    const documentByFormType: Record<string, SigningDocument> = {};
    const documentOptions: Option[] = [];

    formsToSign.forEach(form => {
      const rowKey = getSigningDrawerListRowKey(form);
      const signedDoc = findUserDocumentForSigningForm(
        form,
        signingDrawerLookups,
      );

      const isUpdatingDoc = updatingDocs[rowKey];
      const isSignedDoc =
        signedDoc?.status &&
        ![DocumentStatus.Rejected, DocumentStatus.Signing].includes(
          signedDoc.status,
        );

      documentOptions.push({
        text: form.name,
        value: rowKey,
        disabled: isUpdatingDoc || isSignedDoc,
      });

      documentByFormType[rowKey] = {
        ...form,
        status: signedDoc?.status,
      };
    });
    return { documentOptions, documentByFormType };
  }, [formsToSign, signingDrawerLookups, updatingDocs]);

  const getFormStatusText = useCallback(
    (doc: SigningDocument) => {
      switch (doc?.status) {
        case DocumentStatus.Signing:
          return t('status.signed');
        case DocumentStatus.InReview:
          return t('status.underReview');
        case DocumentStatus.Rejected:
          return t('status.rejected');
        case DocumentStatus.Approved:
          return t('status.approved');
        default:
          return t('status.notSigned');
      }
    },
    [t],
  );

  const onDocumentSelect = () => {
    if (!tempSelected.current) return;

    setDocumentToSign(documentByFormType[tempSelected.current.value]);
    tempSelected.current = undefined;
    hideConfirmingSignAgain();
  };

  const beforeSelectDocument = async (option: Option) => {
    tempSelected.current = option;
    const signingForm = documentByFormType[option.value];
    if (!signingForm) return;

    let signedDoc = findUserDocumentForSigningForm(
      signingForm,
      signingDrawerLookups,
    );

    if (signedDoc?.status === DocumentStatus.Signing) {
      const docAwaitingCompletion = signedDoc;
      setUpdatingDocs(prev => ({
        ...prev,
        [option.value]: true,
      }));
      try {
        const { user: refreshedHolder } =
          await getHolderDetailAndRequiredDocumentsRequest(
            loggedInUser?.operationId || '',
            holderJointMinor?.clientId || 0,
          );
        const targetDoc = refreshedHolder?.documents.find(
          item => item.id === docAwaitingCompletion.id,
        );

        if (targetDoc && targetDoc.status !== DocumentStatus.Signing) {
          dispatch(notifyMinorDataChanged());
          return;
        }

        signedDoc = targetDoc || docAwaitingCompletion;
      } finally {
        setUpdatingDocs(prev => ({
          ...prev,
          [option.value]: false,
        }));
      }

      const timeDifference = differenceInSeconds(
        new Date(),
        new Date(signedDoc.createdAt || '2000-01-01'),
      );

      if (timeDifference >= DOCUMENT_SIGNING_SECONDS) {
        showConfirmingSignAgain();
      }

      return;
    }

    onDocumentSelect();
  };

  const onSigned = () => {
    setDocumentToSign(undefined);
  };

  const renderItemIcon = useCallback(
    (option: Option) => {
      let iconName: 'email' | 'signature' | undefined;
      const status = documentByFormType[option.value].status;

      if (status === DocumentStatus.Signing) {
        iconName = 'email';
      } else if (!status) {
        iconName = 'signature';
      } else {
        iconName = undefined;
      }

      return (
        <StatusIcon
          size={40}
          value={String(status)}
          iconName={iconName}
          isLoading={updatingDocs[option.value]}
        />
      );
    },
    [documentByFormType, updatingDocs],
  );

  const onCloseSigningDocument = () => {
    setDocumentToSign(undefined);
  };

  const emailForSigning =
    operationUser?.allowToDebitCardOnlineBanking &&
    documentToSign?.code !== SigningFormType.FV511
      ? operationUser?.email
      : loggedInUser?.email;

  return (
    <StyledDocumentsSigningDrawer
      {...props}
      title={t('docSigningDrawer.title')}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
    >
      {isLoading ? (
        <CenterSlot>
          <Loader />
        </CenterSlot>
      ) : (
        <>
          <DrawerContainer>
            <PreQuestions
              question={t('docSigningDrawer.preQuestions')}
              options={documentOptions}
              onSelect={beforeSelectDocument}
              renderItemIcon={renderItemIcon}
              renderItemText={option => (
                <ListItemText
                  primary={option.text}
                  secondary={getFormStatusText(
                    documentByFormType[option.value],
                  )}
                />
              )}
              colorTheme="grey"
            />
          </DrawerContainer>
          {documentToSign && (
            <SigningDocumentViewer
              user={{
                email: emailForSigning,
                operationId: operationUser?.operationId,
                id: operationUser?.id,
              }}
              src={documentToSign}
              onClose={onCloseSigningDocument}
              onSigned={onSigned}
            />
          )}
          {isConfirmingSignAgain && (
            <ConfirmDrawer
              title={t('docSigningDrawer.signAgainDrawer.title')}
              description={t('docSigningDrawer.signAgainDrawer.description')}
              primaryAction={onDocumentSelect}
              primaryText={t('docSigningDrawer.signAgainDrawer.primaryText')}
              secondaryAction={hideConfirmingSignAgain}
              secondaryText={t(
                'docSigningDrawer.signAgainDrawer.secondaryText',
              )}
              onClose={hideConfirmingSignAgain}
            />
          )}
        </>
      )}
    </StyledDocumentsSigningDrawer>
  );
}
